import _ from 'lodash';
import { compose } from 'redux';
import { withRouter } from 'react-router-dom';
import PropTypes from 'prop-types';
import React, {
  useState,
  useEffect,
} from 'react';
import { useQuery } from '@apollo/client';
import { ErrorHandler, Loader } from '@playbookux/playbook-web-core';
import { Container, Header } from './styles';

import SurveyPageAnswers from './survey-page-answer';
import { GET_PROJECT_BY_ID, SURVEYS_PAGES_ANSWERS } from './graphql';
import useIdentifySkippedPages from '../../../core/hooks/use-identify-skipped-pages';
import useIdentifySkippedQuestions from '../../../core/hooks/use-identify-skipped-questions';

import withProjectSidebar from '../../components/with-project-sidebar';
import Tabs from '../../constants/tabs';

const ParticipantsResponse = (props) => {
  const {
    project,
    match,
  } = props;

  const { params } = match || {};
  const { projectId, testSessionId } = params || {};

  const surveys = project?.surveys;

  const {
    loading: loadingSurveys,
    data: surveysData = {},
  } = useQuery(GET_PROJECT_BY_ID, {
    variables: {
      id: projectId,
    },
    skip: surveys,
  });

  const {
    data = {},
    loading,
    errors,
  } = useQuery(SURVEYS_PAGES_ANSWERS, {
    variables: {
      testSessionId,
    },
  });

  const [surveysPagesAnswers, setSurveysPagesAnswers] = useState([]);

  useEffect(() => {
    if (!_.isEmpty(data)) {
      const { testerSurveyAnswers: surveyPage } = data;
      setSurveysPagesAnswers(surveyPage);
    }
  }, [data]);

  const surveysPagesAnswersClone = _.cloneDeep(surveysPagesAnswers);

  const [surveysPages] = surveys || surveysData.project.surveys;
  const surveyPages = surveysPages && surveysPages.surveysPages;

  const answersSimplified = surveysPagesAnswers.map((survey) => {
    const { questionId, answers, questionType } = survey;

    if (!answers || answers.length === 0) {
      // if there is no answer for the question
      return { questionId };
    }

    if (questionType !== 'multiple_select') {
      // non-multiple select question
      return {
        questionId,
        questionOptionId: answers[0].id,
      };
    } return {
      questionId,
      questionOptionIds: answers.map((option) => option.id),
    };
  });

  const skippedPages = useIdentifySkippedPages(surveyPages, answersSimplified);
  const skippedQuestions = useIdentifySkippedQuestions(surveyPages, answersSimplified, skippedPages);

  if (loading || loadingSurveys) {
    return (
      <Container>
        <Loader size="small" data-loader="participants-response-loader" />
      </Container>
    );
  }

  skippedPages.forEach((skippedPage) => {
    surveysPagesAnswersClone.push({
      page: skippedPage.pageOrder,
      isSkipped: true,
      skippedPageMessage: skippedPage.skippedPageMessage,
    });
  });

  skippedQuestions.forEach((skippedQuestion) => {
    _.remove(surveysPagesAnswersClone, (answer) => answer.questionId === skippedQuestion.questionId);
    surveysPagesAnswersClone.push({
      page: skippedQuestion.pageOrder,
      isQuestionSkipped: true,
      questionId: skippedQuestion.questionId,
      questionLabel: skippedQuestion.questionLabel,
      questionOrder: skippedQuestion.questionOrder,
      questionType: skippedQuestion.questionType,
      skippedQuestionMessage: skippedQuestion.skippedQuestionMessage,
    });
  });

  let groupedSurveysPagesAnswers = _
    .chain(surveysPagesAnswersClone)
    .groupBy('page')
    .sortBy('page')
    .value();

  // remove the page that has five second question + reindex the page
  groupedSurveysPagesAnswers = groupedSurveysPagesAnswers
    .filter((page) => !page?.some((question) => question?.questionType === 'five_second'));

  if (errors) {
    return (
      <ErrorHandler
        error={errors}
        defaultMessage="Error while retrieving the surveys answers."
      />
    );
  }

  return (
    <Container>
      {surveysPagesAnswers && surveysPagesAnswers.length > 0 && (
        <Header as="h2">{`Survey Responses for ${surveysPagesAnswers[0].firstName}`}</Header>
      )}
      {groupedSurveysPagesAnswers.map((surveyPageAnswers) => (
        <SurveyPageAnswers
          key={surveyPageAnswers[0].pageId}
          surveyPageAnswers={surveyPageAnswers}
        />
      ))}
    </Container>
  );
};

ParticipantsResponse.propTypes = {
  match: PropTypes.shape({
    params: PropTypes.shape({
      projectId: PropTypes.string,
      testSessionId: PropTypes.string,
    }).isRequired,
  }).isRequired,
  project: PropTypes.oneOfType([PropTypes.object]).isRequired,
};

export default compose(
  withRouter,
  withProjectSidebar({
    selectedTab: Tabs.PROJECT,
    showSideBar: false,
  }),
)(ParticipantsResponse);
